/**
 * MobileDrawer Component
 *
 * Hamburger menu drawer for mobile navigation.
 * Opens from the left using shadcn Sheet component.
 * Contains all navigation items, resources, social links, and RPC status.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Settings, User, Instagram, Youtube, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { RpcStatusNav } from '@/components/nav/RpcStatusNav';
import { NAVIGATION_ITEMS, RESOURCE_LINKS, EXTERNAL_LINKS, LEGAL_LINKS, type NavigationItem } from '@/lib/constants/navigation';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { Badge } from '@/components/ui/badge';

// Twitter/X icon
function TwitterIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// TikTok icon
function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.73a8.19 8.19 0 0 0 4.76 1.52V6.79a4.84 4.84 0 0 1-1-.1z" />
    </svg>
  );
}

interface MobileDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MobileDrawer({ open, onOpenChange }: MobileDrawerProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { walletAddress, connected } = useTurnkeyWallet();

  const close = () => onOpenChange(false);

  const isPathActive = (item: NavigationItem) => {
    if (item.path === '/') return location.pathname === '/';
    if (location.pathname.startsWith(item.path)) return true;
    return item.activePaths?.some((p) => location.pathname.startsWith(p)) ?? false;
  };

  const handleProfileClick = () => {
    close();
    if (connected && walletAddress) {
      navigate('/creator/' + walletAddress);
    } else {
      navigate('/profile');
    }
  };

  const isProfileActive = location.pathname === '/profile' || location.pathname.startsWith('/creator/');
  const isSettingsActive = location.pathname.startsWith('/settings');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-[280px] p-0 flex flex-col bg-background border-r border-border"
      >
        {/* Visually hidden title for accessibility */}
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

        {/* Logo Header */}
        <div
          className="h-14 px-4 flex items-center gap-3 border-b border-border shrink-0"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <Link to="/" onClick={close} className="flex items-center">
            <img
              src="/dextoro-logo.png"
              alt="dextoro"
              className="h-7 w-auto"
            />
          </Link>
        </div>

        {/* Navigation Items (scrollable when needed) */}
        <nav className="overflow-y-auto pt-3 px-3 pb-3">
          {/* Primary Navigation */}
          <div className="space-y-0.5">
            {NAVIGATION_ITEMS.map((item) => {
              const isActive = isPathActive(item);
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={close}
                  className={cn(
                    'flex items-center gap-3 px-3 min-h-[48px] rounded-lg transition-colors',
                    isActive
                      ? 'bg-white/10 text-primary font-semibold'
                      : 'text-foreground hover:bg-white/5 hover:text-primary'
                  )}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{item.label}</span>
                  {item.badge && (
                    <Badge
                      variant={(item.badgeVariant as any) || 'default'}
                      pulse={item.badgeVariant === 'live'}
                      className="ml-auto px-1.5 py-px text-[10px]"
                    >
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}

            {/* Profile */}
            <button
              onClick={handleProfileClick}
              className={cn(
                'flex items-center gap-3 px-3 min-h-[48px] rounded-lg transition-colors w-full text-left',
                isProfileActive
                  ? 'bg-white/10 text-primary font-semibold'
                  : 'text-foreground hover:bg-white/5 hover:text-primary'
              )}
            >
              <User className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">Profile</span>
            </button>

            {/* Settings */}
            <Link
              to="/settings"
              onClick={close}
              className={cn(
                'flex items-center gap-3 px-3 min-h-[48px] rounded-lg transition-colors',
                isSettingsActive
                  ? 'bg-white/10 text-primary font-semibold'
                  : 'text-foreground hover:bg-white/5 hover:text-primary'
              )}
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">Settings</span>
            </Link>
          </div>

          {/* Divider */}
          <div className="h-px bg-border my-3 mx-1" />

          {/* Resources */}
          <div className="px-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
              Resources
            </p>
            <div className="flex flex-col gap-1.5">
              {RESOURCE_LINKS.map((link) =>
                link.isExternal ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}
                    className="no-min-touch text-sm text-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={close}
                    className="no-min-touch text-sm text-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                )
              )}
            </div>
          </div>

          {/* Social Links */}
          <div className="flex items-center justify-center gap-4 px-3 pt-6">
            <a
              href={EXTERNAL_LINKS.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Twitter"
            >
              <TwitterIcon className="w-5 h-5" />
            </a>
            <a
              href={EXTERNAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={EXTERNAL_LINKS.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="YouTube"
            >
              <Youtube className="w-5 h-5" />
            </a>
            <a
              href={EXTERNAL_LINKS.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="TikTok"
            >
              <TikTokIcon className="w-5 h-5" />
            </a>
            <a
              href={EXTERNAL_LINKS.email}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Email"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </nav>

        {/* Footer */}
        <div className="shrink-0 border-t border-border px-3 py-2 space-y-1">
          {/* RPC Status */}
          <div className="flex items-center justify-center">
            <RpcStatusNav />
          </div>

          {/* Legal Links with pipe divider */}
          <p className="text-xs text-muted-foreground text-center">
            <a
              href={LEGAL_LINKS.privacy}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              className="hover:text-foreground transition-colors"
            >
              Privacy
            </a>
            <span className="text-muted-foreground/50 mx-2">|</span>
            <a
              href={LEGAL_LINKS.terms}
              target="_blank"
              rel="noopener noreferrer"
              onClick={close}
              className="hover:text-foreground transition-colors"
            >
              Terms
            </a>
          </p>

          {/* Copyright */}
          <p className="text-xs text-muted-foreground/60 text-center">
            © DexToro Trading Inc. 2026
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
